import * as React from "react";

type PromiseState<DataType> =
  | {
      status: "idle";
      data?: null;
      error?: null;
      promise?: null;
    }
  | {
      status: "pending";
      data?: null;
      error?: null;
      promise: Promise<DataType>;
    }
  | {
      status: "resolved";
      data: DataType;
      error: null;
      promise: null;
    }
  | {
      status: "rejected";
      data: null;
      error: Error;
      promise: null;
    };

type PromiseAction<DataType> =
  | { type: "reset" }
  | { type: "pending"; promise: Promise<DataType> }
  | { type: "resolved"; data: DataType; promise?: Promise<DataType> }
  | { type: "rejected"; error: Error; promise: Promise<DataType> };

function promiseReducer<DataType>(
  state: PromiseState<DataType>,
  action: PromiseAction<DataType>,
): PromiseState<DataType> {
  const { type } = action;

  switch (type) {
    case "pending": {
      return {
        status: "pending",
        data: null,
        error: null,
        promise: action.promise,
      };
    }
    case "resolved": {
      if (action.promise !== undefined && action.promise !== state.promise) {
        return state;
      }
      return {
        status: "resolved",
        data: action.data,
        error: null,
        promise: null,
      };
    }
    case "rejected": {
      if (action.promise !== state.promise) {
        return state;
      }
      return {
        status: "rejected",
        data: null,
        error: action.error,
        promise: null,
      };
    }
    case "reset":
      return {
        status: "idle",
        data: null,
        error: null,
      };
    default: {
      const exhaustiveCheck: never = type;
      return exhaustiveCheck;
    }
  }
}

export function usePromise<DataType>() {
  const [promiseState, dispatch] = React.useReducer<
    React.Reducer<PromiseState<DataType>, PromiseAction<DataType>>
  >(promiseReducer, {
    status: "idle",
    data: null,
    error: null,
  });

  const run = React.useCallback((promise: Promise<DataType>) => {
    dispatch({ type: "pending", promise });

    promise.then(
      (promiseData) => {
        dispatch({ type: "resolved", data: promiseData, promise });
      },
      (promiseError) => {
        dispatch({ type: "rejected", error: promiseError, promise });
      },
    );
  }, []);

  const setData = React.useCallback((data: DataType) => {
    dispatch({ type: "resolved", data });
  }, []);

  return { promiseState, run, setData };
}
